/**
 * Shared timing constants for the per-tile animation stack (claim, buyout).
 *
 * ## Design intent
 *
 * The per-tile animation stack supports two primary flows:
 *
 *   1. **Claim** — a light shaft blooms from the tile and settles; the
 *      slab itself never disappears. Only the avatar fades in at the
 *      end. Cheap visually and keeps identity (the tile is already
 *      there, just got a new owner).
 *   2. **Buyout** — the slab *does* break: shake → tile scales to 0
 *      while tile-sized chunks fly + fall → new tile grows back in →
 *      new avatar fades in on top. The `BUYOUT_CHUNKS_MS` /
 *      `BUYOUT_TILE_IN_MS` windows drive the scale transitions in
 *      `TileInstanced`; the debris FX lives in `TileEffectsOverlay`
 *      and the avatar shatter / reveal lives in `TileAvatarOverlay`.
 *
 * Two families of timings keep these paths composable:
 *
 *   1. **Tile-hide timings** (`*_LIGHT_MS`, `*_CHUNKS_MS`, `*_TILE_IN_MS`)
 *      — consumed by `TileInstanced` / `TileMaterialCapOverlay` to
 *      decide when to scale the tile mesh. Setting any of these to 0
 *      disables the corresponding phase (claim leaves all at 0 because
 *      the slab stays put; buyout sets them to non-zero values so the
 *      slab actually fractures).
 *   2. **FX timings** (`*_LIGHT_FX_MS`, `*_CHUNKS_FX_MS`) — consumed
 *      by `TileEffectsOverlay` to drive the visual duration of the
 *      light shaft + falling debris, decoupled from the tile mesh so
 *      the chunks can still be tumbling while the new slab eases back
 *      in underneath them.
 *
 * The avatar reveal (`TileAvatarOverlay`) hooks into `*_TILE_VISIBLE_AT`
 * so the new holder fades in after the slab has fully returned — never
 * on a still-growing mesh.
 *
 * All values are in milliseconds.
 */

// ─── Claim ──────────────────────────────────────────────────────────────────

/**
 * Tile-hide window consumed by `TileInstanced`. Pinned at 0 so the tile
 * stays visible for the whole claim — the vacant → owned transition is
 * carried by the tier-color shift + avatar fade instead of a blink-out.
 */
export const CLAIM_LIGHT_MS = 0;
/**
 * Tile scale-in duration consumed by `TileInstanced`. Pinned at 0 for
 * the same reason as above — the tile never leaves, so there's nothing
 * to grow back in.
 */
export const CLAIM_TILE_IN_MS = 0;

/** Light-shaft visual duration — drives the beam fade in/out inside
 *  `TileEffectsOverlay`. Decoupled from tile visibility so we can keep
 *  the tile mesh on-screen while the FX play. */
export const CLAIM_LIGHT_FX_MS = 900;
/** Avatar fade-in duration — opacity 0 → 1 on the revealed tile. */
export const CLAIM_AVATAR_FADE_MS = 520;

/** Time (ms from `startedAt`) when the avatar is allowed to reveal.
 *  Waits for the beam to mostly settle so the avatar doesn't compete
 *  with the bloom. */
export const CLAIM_TILE_VISIBLE_AT = CLAIM_LIGHT_FX_MS;
/** Time (ms from `startedAt`) when the avatar has fully faded in. */
export const CLAIM_TOTAL_MS = CLAIM_TILE_VISIBLE_AT + CLAIM_AVATAR_FADE_MS;

// ─── Buyout ─────────────────────────────────────────────────────────────────

/**
 * Pre-break shake duration. The tile wobbles in place to sell the
 * incoming takeover before the slab actually fractures.
 */
export const BUYOUT_SHAKE_MS = 260;
/**
 * Tile-hide window after the shake. The slab scales to 0 for this
 * duration so the tile-coloured chunks painted by
 * `TileEffectsOverlay` read as the slab itself breaking apart —
 * nothing else occupies the footprint while the debris flies.
 */
export const BUYOUT_CHUNKS_MS = 600;
/**
 * Tile scale-in after the chunks window. New slab eases back to full
 * size with an ease-out cubic before the new avatar fades in on top.
 */
export const BUYOUT_TILE_IN_MS = 360;

/** How long the chunks debris is on screen (spawn → full fade).
 *  Spans the break + tile-in windows so the last chunks are still
 *  tumbling as the new slab finishes growing in — avoids a sterile
 *  beat where the mesh is settled but the debris has vanished. */
export const BUYOUT_CHUNKS_FX_MS = 960;

/** When the old avatar starts to shatter. Kicks in mid-way through
 *  the shake so the disintegration reads as pre-impact (avatar
 *  crumbles, THEN the slab fractures underneath), not after-the-fact. */
export const BUYOUT_SHATTER_START_MS = 120;
/** How long the avatar shatter animation plays. Lands right around
 *  the end of the slab-break window so the avatar and tile disappear
 *  together rather than one dragging behind the other. */
export const BUYOUT_SHATTER_MS = 720;

/** New avatar fade-in after the new slab has grown back in. */
export const BUYOUT_AVATAR_FADE_MS = 520;

/**
 * End of the tile-hide window. Consumed by `TileInstanced` +
 * `TileMaterialCapOverlay` to flip the slab from scaled-to-0 to the
 * grow-back phase.
 */
export const BUYOUT_CHUNKS_END_AT = BUYOUT_SHAKE_MS + BUYOUT_CHUNKS_MS;
/**
 * Time when the new avatar is allowed to reveal. Waits for the slab
 * to fully grow back so the avatar pops in on a settled tile, not a
 * growing one — matches the claim "fade in on a steady slab" feel.
 */
export const BUYOUT_TILE_VISIBLE_AT = BUYOUT_CHUNKS_END_AT + BUYOUT_TILE_IN_MS;
/** Total buyout animation length. */
export const BUYOUT_TOTAL_MS = BUYOUT_TILE_VISIBLE_AT + BUYOUT_AVATAR_FADE_MS;

// ─── Forfeit ────────────────────────────────────────────────────────────────

/**
 * Visible duration of the dramatic "this seat just got forfeited" beat —
 * the 2D `seat-forfeit-animation` (shake + dissolve + glow) and the 3D
 * steel-chains-wrap. After this window we transition the tile into the
 * `waterfall` state so the gold padlock + countdown ring carries the
 * remainder of the V2.1 anti-snipe priority window. Mirrors the longer
 * of the two CSS keyframes that compose `.seat-forfeit-animation`
 * (`seat-forfeit-glow 1.2s`).
 */
export const FORFEIT_DRAMATIC_MS = 1200;

// Board-swap timings (depart / sweep / arrive for 3D, or the 2D
// Lite fade equivalents) used to live here. The lobby's two-slot
// stage now drives the visible crossfade off `BOARD_CROSSFADE_MS`
// in `src/features/lobby/board-stage/board-stage-timings.ts`, and
// per-tile transient state lives inside each `BoardStageSlot` —
// no canvas-level swap state machine is involved any more, so
// these constants no longer have a consumer.
